/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- pre-existing recording hook importing only the StreamingStatus vocabulary from /contracts (the rule anchors on the first import, hence the placement)
import { agentRecordingSessionIdAtom } from '@/atoms/agentRecordingAtoms';
import {
  backupUploadPromptAtom,
  isCaptureTruncatedPromptFor,
  requireBackupUploadModalAtom,
} from '@/atoms/sessionAtoms';
import { store, type Dispatch } from '@/store';
import { toast } from '@heidi/design-system/toast';
import {
  BackupUploadingStatus,
  GET_SESSION_BY_ID,
  StreamingStatus,
  type QuerySessionResponse,
} from '@heidi/session/contracts';
import {
  useMutation,
  useMutationState,
  useQueryClient,
  type MutationState,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { atom, getDefaultStore, useAtom, useSetAtom } from 'jotai';
import { get } from 'lodash-es';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
// import { base64ToBlob } from 'base64-blob';
import { trackBackupSizeZeroError } from '@/lib/analytics/scribe/general';
import { uploadBackupAudio } from '@/pages/api/scribeAPIV2';
import { recorderFmc } from '@/tauri/controller/recording';
import { isTauri } from '@/tauri/utils';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { GET_BACKUP_AUDIO } from '../query/useQueryBackupAudio';
import {
  releaseCloseGuardAfterRecovery,
  type ReleaseOrigin,
} from './recoveryCloseGuardRelease';

const uploadMessages = defineMessages({
  uploadedElsewhereTitle: {
    defaultMessage: 'Recording uploaded',
    id: 'R3PkSzhQWv',
    description:
      'Toast title when a recording backup finished uploading while the user was on another session',
  },
  uploadedElsewhereBody: {
    defaultMessage: 'Open the session to generate its note.',
    id: 'w/q4ZU2oRc',
    description:
      'Toast text telling the user to open the session to generate its note after the backup finished uploading',
  },
});

/** Input of the backup upload mutation. */
export type UploadBackupInput = {
  sessionId: string;
  audioId: string;
  rawAudio: Blob;
  mimeType: string;
  inBackground?: boolean;
  /** When false, a foreground upload must not dispatch note generation on success. */
  generateNoteOnSuccess?: boolean;
  /**
   * True once the recording is over (a final stop, not a pause or device
   * switch). Only then is the app-quit close guard released. Defaults to
   * false, and is separate from `inBackground`.
   */
  sessionRecordingEnded?: boolean;
  /** Show the recovery prompt if this background upload fails. */
  promptOnFailure?: boolean;
  /** The upload covers a recording that ended early; the prompt warns of that. */
  partialAudio?: boolean;
  path?: string;
};

/** Log message for truncated-capture results; carries a closed `outcome` value only. */
export const TRUNCATED_OUTCOME_LOG = '[TruncatedCapture] outcome';

const UPLOAD_BACKUP_RECORDING = 'uploadBackupRecording';

export const uploadBackupProgressAtom = atom<number>(0);

// Circuit breaker for backup uploads. Terminal (4xx / non-Axios) failures are
// counted per session; at MAX_BACKUP_ATTEMPTS `mutationFn` skips the upload so
// a re-trigger cannot keep sending a backup the server rejects. The counter
// clears on success and resets on reload.
const failedBackupAttempts = new Map<string, number>();
const MAX_BACKUP_ATTEMPTS = 5;

/** Thrown by `mutationFn` to skip an upload when the session's circuit is open. */
class BackupCircuitOpenError extends Error {
  constructor(sessionId: string) {
    super(`backup upload circuit open for session ${sessionId}`);
    this.name = 'BackupCircuitOpenError';
  }
}

/**
 * Classifies HTTP errors as retryable (transient) or terminal (permanent).
 * Mirrors the Rust `UploaderError::is_retryable()` logic in
 * `src-tauri/src/session_creation_engine/uploader.rs`.
 *
 * Retryable: network errors, 408/429/5xx (server-side transient issues)
 * Terminal: 400/401/403/404 (client errors, bad request, auth, not found)
 */
function isRetryableError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) {
    // Non-Axios errors (e.g. JS exceptions) are not retryable
    return false;
  }

  const status = error.response?.status;
  if (!status) {
    // Network error (no response) → retryable
    return true;
  }

  // Retryable: 408 (Request Timeout), 429 (Too Many Requests), 5xx (Server Error)
  // Terminal: 400 (Bad Request), 401 (Unauthorized), 403 (Forbidden), 404 (Not Found)
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

/**
 * Session ids of backup uploads that are running now. With `foregroundOnly`,
 * background uploads (pause, device switch) are left out.
 */
export const getPendingUploadSessionIds = (
  states: readonly Pick<
    MutationState<unknown, Error, UploadBackupInput>,
    'status' | 'variables'
  >[],
  foregroundOnly = false
): string[] =>
  states
    .filter((state) => state.status === 'pending' && state.variables)
    .filter((state) => !(foregroundOnly && state.variables?.inBackground))
    .map((state) => state.variables?.sessionId)
    .filter((id): id is string => Boolean(id));

export function useMutateUploadBackupRecording() {
  const dispatch = useDispatch<Dispatch>();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const setUploadRequiredModal = useSetAtom(requireBackupUploadModalAtom);
  const setBackupUploadPrompt = useSetAtom(backupUploadPromptAtom);
  const [, setUploadBackupProgress] = useAtom(uploadBackupProgressAtom);

  /**
   * Hands the outcome to the shared close-guard release. `sessionRecordingEnded`
   * is the caller's intent at stop time; the gate re-checks liveness when it
   * writes. Unset means the consultation continues and nothing is released.
   */
  const releaseCloseGuard = (
    input: { sessionId: string; sessionRecordingEnded?: boolean },
    origin: ReleaseOrigin
  ): Promise<boolean> =>
    releaseCloseGuardAfterRecovery({
      sessionId: input.sessionId,
      // No `recordingId`: this path only has the FE-minted `audioId`.
      consultationIntent: input.sessionRecordingEnded ? 'ended' : 'continues',
      queryClient,
      origin,
    });

  const updateSessionBackupUploadStatus = (
    sessionId: string,
    status: BackupUploadingStatus
  ) => {
    queryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (oldData) => (oldData ? { ...oldData, backupUploadingStatus: status } : undefined)
    );
  };

  // Whether this session is recording right now (the chunk state holds one session).
  const isRecordingSession = (sessionId: string) =>
    store.getState().timerModel.isRunning &&
    store.getState().tauriRecordModel.chunks.session_id === sessionId;

  // On screen: the route session, or the session the Projects overlay is bound to.
  const isSessionOnScreen = (sessionId: string) =>
    getCurrentSessionId() === sessionId ||
    getDefaultStore().get(agentRecordingSessionIdAtom) === sessionId;

  const releaseOptimisticUploading = (sessionId: string) => {
    queryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (oldData) =>
        oldData?.streamingStatus === StreamingStatus.UPLOADING
          ? { ...oldData, streamingStatus: StreamingStatus.NOT_STARTED }
          : oldData
    );
  };

  /**
   * A failed upload that should be shown to the user (a foreground upload, or a
   * background one with `promptOnFailure`). It releases the stop's optimistic
   * `UPLOADING`, marks the session FAILED, and raises the recovery prompt for a
   * user who closed the dialog. An open prompt is never replaced.
   */
  const settleSurfacedBackupFailure = (sessionId: string, partialAudio = false) => {
    updateSessionBackupUploadStatus(sessionId, BackupUploadingStatus.FAILED);
    // If the truncated-capture prompt is open, note the failure for its Close.
    setBackupUploadPrompt((current) =>
      isCaptureTruncatedPromptFor(current, sessionId)
        ? { ...current, uploadFailed: true }
        : current
    );
    if (partialAudio) logger.info(TRUNCATED_OUTCOME_LOG, { outcome: 'uploadFailed' });
    // Do not raise a prompt over a recording that is running again.
    if (!isRecordingSession(sessionId)) {
      // Only fills an empty slot: a prompt for another session stays.
      if (partialAudio) {
        setBackupUploadPrompt(
          (current) => current ?? { kind: 'frontendUpload', sessionId, partial: true }
        );
      } else {
        setUploadRequiredModal((current) => current ?? sessionId);
      }
    }
    releaseOptimisticUploading(sessionId);
  };

  const setBackupProgressCallback = (backupProgress: number) => {
    setUploadBackupProgress(backupProgress);
    logger.debug('upload progress: ' + backupProgress);
  };

  return useMutation({
    retry: (failureCount, error) => {
      // Retry transient errors only (5xx, 429, 408, network).
      if (!isRetryableError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    networkMode: isTauri() ? 'always' : 'offlineFirst',
    mutationKey: [UPLOAD_BACKUP_RECORDING],
    mutationFn: async (input: UploadBackupInput) => {
      const { sessionId, rawAudio, mimeType } = input;

      // Circuit breaker: skip the upload once the session hit the terminal-failure
      // cap. The thrown sentinel is ignored by `onError` and is not retryable.
      if ((failedBackupAttempts.get(sessionId) ?? 0) >= MAX_BACKUP_ATTEMPTS) {
        logger.debug('[BackupUpload] Circuit open — skipping backup upload', {
          sessionId,
        });
        throw new BackupCircuitOpenError(sessionId);
      }

      setUploadBackupProgress(0);

      if (rawAudio.size === 0) {
        trackBackupSizeZeroError({
          sessionId,
        });
      }

      const fileExtensionMatch = mimeType?.match(/^audio\/([^;]+)/);
      const fileExtension = fileExtensionMatch ? fileExtensionMatch[1] : undefined;

      // const url = window.URL.createObjectURL(rawAudio);
      // window.open(url);
      updateSessionBackupUploadStatus(sessionId, BackupUploadingStatus.UPLOADING);
      const startTime = store.getState().timerModel.startTime;
      const seconds = store.getState().timerModel.seconds;
      await uploadBackupAudio({
        sessionId,
        file: rawAudio,
        fileExtension,
        uploadProgressCallback: setBackupProgressCallback,
        startTime,
        endTime: startTime ? startTime + seconds * 1000 : null,
      });
      updateSessionBackupUploadStatus(sessionId, BackupUploadingStatus.SUCCESS);
      return;

      // OLD IMPLEMENTATION: AWS DIRECT UPLOAD
      // updateSessionBackupUploadStatus(sessionId, BackupUploadingStatus.PENDING);
      // const { data: awsEndpoint } = await getBackupAudioUrl(sessionId, fileExtension);

      // updateSessionBackupUploadStatus(sessionId, BackupUploadingStatus.UPLOADING);

      // const headers = {
      //   'Content-Type': fileExtension ? `audio/${fileExtension}` : 'audio/wav',
      // };
      // await axios.put(awsEndpoint.url, rawAudio, { headers });
    },
    onSuccess: async (_, variables) => {
      const { sessionId, inBackground, path, generateNoteOnSuccess } = variables;
      void queryClient.invalidateQueries({
        queryKey: [GET_BACKUP_AUDIO, { sessionId }],
      });
      // Clear the prompt only if it belongs to this session. The truncated-capture
      // prompt stays until the clinician closes it.
      setBackupUploadPrompt((current) => {
        if (current?.sessionId !== sessionId) return current;
        if (current.kind === 'captureTruncated') return current;
        return null;
      });

      // Clear the circuit breaker counter on success
      failedBackupAttempts.delete(sessionId);

      const shouldGenerateNote =
        Boolean(sessionId) && !inBackground && generateNoteOnSuccess !== false;
      if (shouldGenerateNote && isSessionOnScreen(sessionId)) {
        // generateBackupTranscript({ sessionId });
        dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
        updateSessionBackupUploadStatus(sessionId, BackupUploadingStatus.SUCCESS);
      } else if (shouldGenerateNote) {
        // The user left this session: a note started now could open its picker
        // on another one.
        logger.warn('[BackupUpload] note not started, session is not on screen', {
          sessionId,
        });
        releaseOptimisticUploading(sessionId);
        toast({
          title: intl.formatMessage(uploadMessages.uploadedElsewhereTitle),
          description: intl.formatMessage(uploadMessages.uploadedElsewhereBody),
        });
      }

      // Before the awaited release: note generation polls `backup_uploaded`, and
      // the release waits on the network.
      if (isTauri()) {
        if (path) {
          void recorderFmc.remove_file_from_path(path);
        }
        // Skip if a newer recording owns the chunk state (late success of an
        // earlier session). That session then waits on the chunk read or the
        // wait timeout instead.
        const activeChunkSessionId = store.getState().tauriRecordModel.chunks.session_id;
        if (!activeChunkSessionId || activeChunkSessionId === sessionId) {
          dispatch.tauriRecordModel.editBackupUploaded(true);
        }
      }

      // Outside the note-generation gate: a recovery that generates nothing has
      // still finished, and `sessionRecordingEnded` decides the release. Awaited,
      // so callers see the server write.
      //
      // MOBILE-PARITY-DEBT(DSK-944): the backend should own the IDLE transition;
      // iOS/Android recovery does not write it. Remove this when it does.
      await releaseCloseGuard(variables, 'backupUploadSucceeded');
    },
    onError: (error, variables) => {
      const { sessionId, inBackground, promptOnFailure, partialAudio } = variables;
      const surfaceFailure = !inBackground || promptOnFailure;

      // A circuit-open skip is not a real failure: no log, count or toast. A
      // foreground attempt still flips the session to FAILED so the modal shows
      // recovery.
      if (error instanceof BackupCircuitOpenError) {
        if (sessionId && surfaceFailure) {
          settleSurfacedBackupFailure(sessionId, Boolean(partialAudio));
        }
        // An open circuit is final: no more attempts for this backup. Release
        // the guard without awaiting, so the toast and next count are not delayed.
        void releaseCloseGuard(variables, 'backupUploadWillNotRetry');
        return;
      }

      logger.error('failed to upload:', {
        sessionId,
        inBackground: Boolean(inBackground),
        promptOnFailure: Boolean(promptOnFailure),
        errorKind: bridgeErrorKind(error),
        error: sanitizeError(error),
      });

      if (sessionId && surfaceFailure) {
        settleSurfacedBackupFailure(sessionId, Boolean(partialAudio));
      }

      // A non-retryable failure will fail the same way every time, so the
      // recovery is over and the close guard is released. A retryable failure
      // releases nothing: a modal or native retry can still run. Not awaited.
      if (!isRetryableError(error)) {
        void releaseCloseGuard(variables, 'backupUploadWillNotRetry');
      }

      // Count terminal (4xx / non-Axios) failures per session. At
      // MAX_BACKUP_ATTEMPTS the circuit opens and `mutationFn` skips the upload.
      const isTerminal = !isRetryableError(error);
      if (isTerminal) {
        const attempts = (failedBackupAttempts.get(sessionId) || 0) + 1;
        failedBackupAttempts.set(sessionId, attempts);

        logger.warn('[BackupUpload] Terminal backup-upload error', {
          sessionId,
          attempts,
          maxAttempts: MAX_BACKUP_ATTEMPTS,
          status: error instanceof AxiosError ? error.response?.status : 'unknown',
        });

        if (attempts >= MAX_BACKUP_ATTEMPTS) {
          logger.warn(
            '[BackupUpload] Circuit breaker tripped — further uploads for this session are skipped until success or reload',
            { sessionId, attempts }
          );
        }
      }

      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to sync session information',
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
}

export function useMutateStateUploadBackupRecording() {
  const mutationState = useMutationState({
    filters: { mutationKey: [UPLOAD_BACKUP_RECORDING] },
    // This key is only used by this mutation, so its variables are the input.
    select: (mutation) =>
      mutation.state as MutationState<unknown, Error, UploadBackupInput>,
  });

  const currentMutation = mutationState.at(-1);
  const { mutate } = useMutateUploadBackupRecording();

  return {
    isPending: currentMutation?.status === 'pending',
    // Sessions with an upload running now, and the subset that runs in the
    // foreground.
    pendingUploadSessionIds: getPendingUploadSessionIds(mutationState),
    foregroundUploadSessionIds: getPendingUploadSessionIds(mutationState, true),
    isSuccess: currentMutation?.status === 'success',
    isFailed: currentMutation?.status === 'error',
    isPaused: currentMutation?.isPaused,
    data: currentMutation?.data as string,
    mutate,
  };
}
